/**
 * A vehicle's track: timestamped fixes in, a position out on every frame
 * (mobile plan M7). Lifted from InGo's console `src/maps/motion.ts`, itself
 * KangaruRide's `packages/passenger-core/src/motion.ts` (ADR-0091 §1),
 * without the heading: the app draws the rider as a dot.
 *
 * Every map here is polled, and a polled marker teleports: it sits still for
 * the poll interval, then jumps. This is the continuity half:
 *
 * - The marker glides from **wherever it is now** to each new fix over the
 *   learned report interval (the gap between the last two fixes' own
 *   timestamps, clamped to 1-30 s), so it arrives as the next fix is due.
 * - A fix that lands early retargets from the current point, never a
 *   restart from the previous fix (that would read as a vehicle reversing).
 * - Past the newest fix it carries on at the speed the last two fixes imply
 *   for at most one learned interval, then holds; a stationary vehicle
 *   settles the moment it arrives.
 *
 * Two clocks: `fix.at` is the source's (the rider's `recorded_at`) and learns
 * the cadence and speed; `sample(now)` takes the watcher's
 * (`performance.now()`) and paces the glide. They are never compared.
 * Pure: no DOM, no timers, no `Date`.
 */

export const MIN_INTERVAL_MS = 1000;
export const MAX_INTERVAL_MS = 30000;

/**
 * @param {{ maxExtrapolationIntervals?: number }} [options]
 */
export function createTrack(options = {}) {
  const maxExtrapolation = Math.max(0, options.maxExtrapolationIntervals ?? 1);

  let previous = null;
  let newest = null;
  let pending = false;
  let placed = null;
  let glide = null;

  function positionAt(now) {
    if (glide === null) return { point: placed ?? [0, 0], settled: true };

    const { origin, target, startedAt, interval, velocity } = glide;
    const elapsed = Math.max(0, now - startedAt);
    if (elapsed < interval) {
      const p = elapsed / interval;
      return { point: [origin[0] + (target[0] - origin[0]) * p, origin[1] + (target[1] - origin[1]) * p], settled: false };
    }

    const cap = maxExtrapolation * interval;
    const still = velocity[0] === 0 && velocity[1] === 0;
    if (still || cap === 0) return { point: target, settled: true };

    const extra = Math.min(elapsed - interval, cap);
    return { point: [target[0] + velocity[0] * extra, target[1] + velocity[1] * extra], settled: extra >= cap };
  }

  function begin(now) {
    pending = false;
    if (newest === null) return;

    // The first fix is a place, not a journey: the marker appears there.
    if (previous === null) {
      placed = [newest.lng, newest.lat];
      return;
    }

    const span = newest.at - previous.at;
    glide = {
      // Read before the glide is replaced: wherever the marker is now.
      origin: positionAt(now).point,
      target: [newest.lng, newest.lat],
      startedAt: now,
      interval: Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, span)),
      velocity: span > 0 ? [(newest.lng - previous.lng) / span, (newest.lat - previous.lat) / span] : [0, 0],
    };
  }

  return {
    /** @param {{ lng: number, lat: number, at: number }} fix */
    push(fix) {
      if (!Number.isFinite(fix?.lng) || !Number.isFinite(fix?.lat) || !Number.isFinite(fix?.at)) return;
      // A re-served poll or an oldest-first backlog: nothing new to move toward.
      if (newest !== null && fix.at <= newest.at) return;
      // The same place again is "still here": the time moves, the glide does not restart.
      if (newest !== null && fix.lng === newest.lng && fix.lat === newest.lat) {
        newest = fix;
        return;
      }
      previous = newest;
      newest = fix;
      pending = true;
    },

    /** @returns {{ lng: number, lat: number, settled: boolean }} */
    sample(now) {
      if (pending) begin(now);
      const { point, settled } = positionAt(now);
      return { lng: point[0], lat: point[1], settled };
    },

    isMoving(now) {
      if (newest === null) return false;
      if (pending) return true;
      return !positionAt(now).settled;
    },

    latest() {
      return newest;
    },
  };
}
